import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { NominacionMercadoSecundarioService } from '../../core/services/nominacion-mercado-secundario/nominacion-mercado-secundario.service';
import {
  IInfoContratoNominacion,
  INominacionMercadoSecundario,
  INominacionMercadoSecundarioOptions,
} from '../../core/interfaces/nominacion-mercado-secundario.interface';
import {
  GetNominacionOptionsAction,
  GetNominacionesByContratoAction,
  CreateNominacionAction,
  UpdateNominacionAction,
  DeleteNominacionAction,
  ClearNominacionesAction,
} from './nominacion-mercado-secundario.actions';

export interface NominacionMercadoSecundarioStateModel {
  nominaciones: INominacionMercadoSecundario[];
  infoContrato: IInfoContratoNominacion | null;
  options: INominacionMercadoSecundarioOptions;
  currentContratoUuid: string | null;
  currentFecha: string | null;
  loading: boolean;
}

@State<NominacionMercadoSecundarioStateModel>({
  name: 'nominacionMercadoSecundario',
  defaults: {
    nominaciones: [],
    infoContrato: null,
    options: {},
    currentContratoUuid: null,
    currentFecha: null,
    loading: false,
  },
})
@Injectable()
export class NominacionMercadoSecundarioState {
  @Selector()
  static GetAll(
    state: NominacionMercadoSecundarioStateModel
  ): INominacionMercadoSecundario[] {
    return state?.nominaciones ?? [];
  }

  @Selector()
  static GetOptions(
    state: NominacionMercadoSecundarioStateModel
  ): INominacionMercadoSecundarioOptions {
    return state?.options ?? {};
  }

  @Selector()
  static IsLoading(state: NominacionMercadoSecundarioStateModel): boolean {
    return state?.loading ?? false;
  }

  @Selector()
  static GetCurrentContratoUuid(
    state: NominacionMercadoSecundarioStateModel
  ): string | null {
    return state?.currentContratoUuid ?? null;
  }

  @Selector()
  static GetInfoContrato(
    state: NominacionMercadoSecundarioStateModel
  ): IInfoContratoNominacion | null {
    return state?.infoContrato ?? null;
  }

  constructor(
    private nominacionService: NominacionMercadoSecundarioService,
    private dialog: MatDialog
  ) {}

  @Action(GetNominacionOptionsAction)
  async GetNominacionOptionsAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>,
    { payload }: GetNominacionOptionsAction
  ) {
    try {
      const options = await this.nominacionService.getOptions(
        payload.contratoMercadoSecundarioUuid,
        payload.puntoSalidaUuid,
        payload.demandaUuid
      );
      ctx.patchState({ options });
    } catch {
      ctx.patchState({ options: {} });
    }
  }

  @Action(GetNominacionesByContratoAction)
  async GetNominacionesByContratoAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>,
    { payload }: GetNominacionesByContratoAction
  ) {
    const contratoUuid =
      typeof payload === 'string' ? payload : payload.contratoUuid;
    const fecha =
      typeof payload === 'string' ? undefined : (payload.fecha ?? undefined);
    ctx.patchState({
      loading: true,
      currentContratoUuid: contratoUuid,
      currentFecha: fecha ?? null,
    });
    try {
      const resp = await this.nominacionService.getNominacionesByContrato(
        contratoUuid,
        fecha
      );
      ctx.patchState({
        nominaciones: resp.nominaciones,
        infoContrato: resp.infoContrato,
        loading: false,
      });
    } catch {
      ctx.patchState({ nominaciones: [], infoContrato: null, loading: false });
    }
  }

  @Action(CreateNominacionAction)
  async CreateNominacionAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>,
    { payload }: CreateNominacionAction
  ) {
    try {
      const result = await this.nominacionService.createNominacion(payload);
      if (result.status === false) {
        throw (result as { err: unknown; status: false }).err;
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'success',
          text: 'La nominación se ha creado de forma exitosa!',
          hiddeCancelBtn: true,
        },
      });
      const { currentContratoUuid, currentFecha } = ctx.getState();
      ctx.dispatch(
        new GetNominacionesByContratoAction({
          contratoUuid:
            currentContratoUuid ?? payload.contratoMercadoSecundarioUuid,
          fecha: currentFecha,
        })
      );
    } catch (err: unknown) {
      this.handleError(err, 'Ha ocurrido un error al crear la nominación.');
    }
  }

  @Action(UpdateNominacionAction)
  async UpdateNominacionAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>,
    { payload }: UpdateNominacionAction
  ) {
    try {
      const result = await this.nominacionService.updateNominacion(
        payload.uuid,
        payload.data
      );
      if (result.status === false) {
        throw (result as { err: unknown; status: false }).err;
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'success',
          text: 'La nominación se ha actualizado de forma exitosa!',
          hiddeCancelBtn: true,
        },
      });
      const { currentContratoUuid, currentFecha } = ctx.getState();
      if (currentContratoUuid) {
        ctx.dispatch(
          new GetNominacionesByContratoAction({
            contratoUuid: currentContratoUuid,
            fecha: currentFecha,
          })
        );
      }
    } catch (err: unknown) {
      this.handleError(
        err,
        'Ha ocurrido un error al actualizar la nominación.'
      );
    }
  }

  @Action(DeleteNominacionAction)
  async DeleteNominacionAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>,
    { payload }: DeleteNominacionAction
  ) {
    try {
      const result = await this.nominacionService.deleteNominacion(payload);
      if (result !== true) {
        throw (result as { err: unknown; status: false }).err;
      }
      this.dialog.open(EnovaAlertsComponent, {
        width: 'auto',
        data: {
          type: 'success',
          text: 'La nominación se ha eliminado de forma exitosa!',
          hiddeCancelBtn: true,
        },
      });
      const { currentContratoUuid, currentFecha } = ctx.getState();
      if (currentContratoUuid) {
        ctx.dispatch(
          new GetNominacionesByContratoAction({
            contratoUuid: currentContratoUuid,
            fecha: currentFecha,
          })
        );
      }
    } catch (err: unknown) {
      this.handleError(err, 'Ha ocurrido un error al eliminar la nominación.');
    }
  }

  @Action(ClearNominacionesAction)
  ClearNominacionesAction(
    ctx: StateContext<NominacionMercadoSecundarioStateModel>
  ) {
    ctx.patchState({
      nominaciones: [],
      infoContrato: null,
      options: {},
      currentContratoUuid: null,
      currentFecha: null,
      loading: false,
    });
  }

  private handleError(err: unknown, defaultMessage: string): void {
    const anyErr = err as {
      error?: { details?: string | string[] };
      details?: string | string[];
    };
    let errorText = defaultMessage;
    const details = anyErr?.error?.details ?? anyErr?.details;
    if (details) {
      errorText =
        typeof details === 'string'
          ? details
          : Array.isArray(details)
            ? details.join('\n')
            : errorText;
    }
    this.dialog.open(EnovaAlertsComponent, {
      width: 'auto',
      data: { type: 'error', text: errorText, hiddeCancelBtn: true },
    });
  }
}
